import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

/**
 * 基础学科与学术教育 - 横屏「现代课件」风格
 *
 * 参考 Apple Keynote / TED 演讲视觉，干净留白 + 大字 + 图解
 * 适合：概念讲解、定义辨析、原理可视化
 * 尺寸：1920x1080 横屏
 */

interface SlideSection {
  label: string;
  content: string;
  visual?: string; // emoji as visual placeholder
}

interface AcademicSlideProps {
  pageNumber: string;
  subject: string;
  title: string;
  definition: string;
  sections: SlideSection[];
  keyVisual: { emoji: string; caption: string };
  bottomNote: string;
  accentColor?: string;
}

const DEFAULT_PROPS: AcademicSlideProps = {
  pageNumber: '03',
  subject: '生物学 · 细胞',
  title: '有丝分裂的四个时期',
  definition: '有丝分裂是真核细胞分裂的主要方式，确保遗传信息精确复制并均等分配',
  sections: [
    { label: '前期', content: '染色质→染色体，核膜解体，纺锤体形成', visual: '🧬' },
    { label: '中期', content: '染色体排列在赤道板上，纺锤丝与着丝点连接', visual: '📍' },
    { label: '后期', content: '着丝点分裂，姐妹染色单体分开移向两极', visual: '↔️' },
    { label: '末期', content: '核膜重建，染色体→染色质，细胞板形成', visual: '🔬' },
  ],
  keyVisual: { emoji: '🧫', caption: '一次有丝分裂 ≈ 1-2 小时' },
  bottomNote: '有丝分裂保证了子细胞与母细胞遗传组成相同',
  accentColor: '#0d9488',
};

export const AcademicSlideTemplate: React.FC<Partial<AcademicSlideProps>> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const accent = p.accentColor!;

  // Intro (0-40)
  const introProgress = spring({ frame, fps, config: { damping: 20, stiffness: 80 } });

  // Definition (30-70)
  const defOpacity = interpolate(frame, [30, 55], [0, 1], { extrapolateRight: 'clamp' });

  // Sections (80+)
  const secStart = 80;
  const secInterval = 60;

  // Key visual
  const kvStart = secStart + p.sections.length * secInterval + 20;
  const kvProgress = spring({ frame: frame - kvStart, fps, from: 0, to: 1, config: { damping: 14, stiffness: 100 } });

  // Bottom note
  const noteStart = kvStart + 50;
  const noteOpacity = interpolate(frame, [noteStart, noteStart + 25], [0, 1], { extrapolateRight: 'clamp' });

  return (
    <AbsoluteFill
      style={{
        background: '#ffffff',
        fontFamily: '-apple-system, "PingFang SC", "SF Pro Display", sans-serif',
        color: '#1a1a1a',
        overflow: 'hidden',
      }}
    >
      {/* Subtle corner accent */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: 400,
          height: 400,
          background: `radial-gradient(circle at top right, ${accent}08, transparent 70%)`,
        }}
      />

      {/* Page number */}
      <div
        style={{
          position: 'absolute',
          top: 48,
          right: 60,
          fontSize: 80,
          fontWeight: 900,
          color: `${accent}12`,
          fontFamily: '"Inter", "Helvetica Neue", sans-serif',
          opacity: introProgress,
        }}
      >
        {p.pageNumber}
      </div>

      {/* Subject tag + title */}
      <div
        style={{
          position: 'absolute',
          top: 55,
          left: 80,
          opacity: introProgress,
          transform: `translateY(${(1 - introProgress) * -20}px)`,
        }}
      >
        <div
          style={{
            display: 'inline-block',
            padding: '6px 18px',
            background: `${accent}12`,
            borderRadius: 8,
            fontSize: 30,
            fontWeight: 600,
            color: accent,
            letterSpacing: '0.05em',
            marginBottom: 16,
          }}
        >
          {p.subject}
        </div>
        <h1
          style={{
            fontSize: 56,
            fontWeight: 800,
            margin: 0,
            lineHeight: 1.2,
            letterSpacing: '-0.02em',
            color: '#111',
          }}
        >
          {p.title}
        </h1>
      </div>

      {/* Definition bar */}
      <div
        style={{
          position: 'absolute',
          top: 230,
          left: 80,
          right: 80,
          opacity: defOpacity,
        }}
      >
        <div
          style={{
            padding: '20px 28px',
            background: '#f8fafc',
            borderRadius: 14,
            borderLeft: `4px solid ${accent}`,
            fontSize: 26,
            lineHeight: 1.6,
            color: '#475569',
          }}
        >
          {p.definition}
        </div>
      </div>

      {/* Sections — horizontal timeline cards */}
      <div
        style={{
          position: 'absolute',
          top: 350,
          left: 80,
          right: 80,
          display: 'flex',
          gap: 20,
        }}
      >
        {p.sections.map((sec, i) => {
          const start = secStart + i * secInterval;
          const cardProgress = spring({
            frame: frame - start,
            fps,
            from: 0,
            to: 1,
            config: { damping: 14, stiffness: 120, mass: 0.6 },
          });
          const isActive = frame >= start && frame < start + secInterval;

          return (
            <div
              key={i}
              style={{
                flex: 1,
                opacity: cardProgress,
                transform: `translateY(${(1 - cardProgress) * 30}px)`,
              }}
            >
              {/* Connector line */}
              {i > 0 && (
                <div
                  style={{
                    position: 'absolute',
                    top: 28,
                    left: -10,
                    width: 20,
                    height: 2,
                    background: `${accent}30`,
                  }}
                />
              )}

              <div
                style={{
                  padding: '28px 24px',
                  background: isActive ? `${accent}08` : '#fafafa',
                  borderRadius: 18,
                  border: isActive ? `2px solid ${accent}40` : '1px solid #eee',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {/* Visual */}
                <div style={{ fontSize: 44, marginBottom: 12 }}>{sec.visual}</div>

                {/* Step label */}
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    marginBottom: 10,
                  }}
                >
                  <div
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: 8,
                      background: accent,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      fontSize: 14,
                      fontWeight: 700,
                      color: 'white',
                    }}
                  >
                    {i + 1}
                  </div>
                  <span style={{ fontSize: 28, fontWeight: 700, color: accent }}>{sec.label}</span>
                </div>

                <div style={{ fontSize: 26, lineHeight: 1.6, color: '#64748b' }}>{sec.content}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Key visual */}
      <div
        style={{
          position: 'absolute',
          bottom: 140,
          left: 80,
          display: 'flex',
          alignItems: 'center',
          gap: 20,
          opacity: kvProgress,
          transform: `scale(${0.9 + kvProgress * 0.1})`,
        }}
      >
        <div
          style={{
            width: 80,
            height: 80,
            borderRadius: 24,
            background: `${accent}10`,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            fontSize: 44,
          }}
        >
          {p.keyVisual.emoji}
        </div>
        <div style={{ fontSize: 26, color: '#94a3b8' }}>{p.keyVisual.caption}</div>
      </div>

      {/* Bottom note */}
      <div
        style={{
          position: 'absolute',
          bottom: 55,
          left: 80,
          right: 80,
          opacity: noteOpacity,
        }}
      >
        <div
          style={{
            fontSize: 26,
            color: '#94a3b8',
            padding: '14px 0',
            borderTop: '1px solid #f1f5f9',
          }}
        >
          💡 {p.bottomNote}
        </div>
      </div>

      {/* Slim progress */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, 480], [0, 100], { extrapolateRight: 'clamp' })}%`,
          height: 3,
          background: accent,
        }}
      />
    </AbsoluteFill>
  );
};
